import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { customFieldFormSchema } from "../validations";
import * as Components from "../../../components";
import {
  useCreateCustomFieldMutation,
  useGetCustomFieldQuery,
  useUpdateCustomFieldMutation,
} from "../services";
import { FormContainer } from "../Shared/FormContainer";
import { mergeObjects } from "../../../shared/helper";
import { useForm } from "../../../hooks";
import { getLocalizedString } from "../../../shared/translation";

export const CustomFieldForm = ({ idToEdit = null, closeForm }) => {
  const [initialValues, setInitialValues] = useState({
    name: "",
    label: "",
    field_type: "string", // Default to string
    asset_types: ["Vehicle"], // Default to Vehicle array
    placeholder: "",
    min: "",
    max: "",
    regex_pattern: "",
    required: false,
  });

  const [regexTemplate, setRegexTemplate] = useState("custom");
  const [regexError, setRegexError] = useState("");

  const regexTemplates = [
    { id: "custom", name: "Custom", pattern: "" },
    { id: "email", name: "Email", pattern: "^\\w[\\w.-]*@([\\w-]+\\.)+[\\w-]+$" },
    { id: "phone", name: "Phone Number", pattern: "^(?:\\+?\\d{1,3}[- ]?)?(?:\\d{8,15})$" },
    { id: "url", name: "URL", pattern: "^(ftp|http|https):\\/\\/(\\w+:{0,1}\\w*@)?(\\S+)(:[0-9]+)?(\\/|\\/([\\/\\w#!:.?+=&%@!\\-/]))?$" },
    { id: "time_12h", name: "12h Time", pattern: "^(0?[1-9]|1[012]):[0-5][0-9](:[0-5][0-9])?\\s*[aApP][mM]$" },
    { id: "time_24h", name: "24h Time", pattern: "^(0?[0-9]|1[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$" },
  ];

  const handleRegexTemplateChange = (templateId) => {
    setRegexTemplate(templateId);
    const template = regexTemplates.find(t => t.id === templateId);
    if (template) {
      formik.setFieldValue("regex_pattern", template.pattern);
      setRegexError("");
    }
  };

  const validateRegexPattern = (pattern) => {
    if (!pattern) {
      setRegexError("");
      return;
    }
    try {
      new RegExp(pattern);
      setRegexError("");
    } catch (e) {
      setRegexError("Invalid regex pattern");
    }
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: customFieldFormSchema,
    onSubmit: (values) => handleFormSubmit(values),
  });

  const { create: createCustomField, update: updateCustomField } = useForm({
    createMutation: useCreateCustomFieldMutation,
    updateMutation: useUpdateCustomFieldMutation,
    closeForm,
    setError: formik.setErrors,
  });

  const { data } = useGetCustomFieldQuery({ id: idToEdit }, { skip: !idToEdit });

  // Set form data in case of edit
  useEffect(() => {
    if (idToEdit && data) {
      setInitialValues(mergeObjects(formik.initialValues, data));
    }
  }, [data]);

  const handleFormSubmit = (formData) => {
    // Validate regex pattern before submission
    if (formData.regex_pattern) {
      try {
        new RegExp(formData.regex_pattern);
      } catch (e) {
        setRegexError("Invalid regex pattern");
        return;
      }
    }

    // Convert empty strings to null for numeric fields
    const payload = {
      ...formData,
      min: formData.min === "" ? null : formData.min,
      max: formData.max === "" ? null : formData.max,
      regex_pattern: formData.regex_pattern || null,
    };

    if (idToEdit) {
      updateCustomField({ formData: payload, id: idToEdit });
    } else {
      createCustomField({ formData: payload });
    }
  };

  // Asset type options (only Vehicle for now)
  const assetTypeOptions = [
    { id: "Vehicle", name: getLocalizedString("vehicle", "Vehicle") },
  ];

  // Field type options (only string for now)
  const fieldTypeOptions = [
    { id: "string", name: getLocalizedString("string", "String") },
  ];

  return (
    <FormContainer
      resourceName="Custom Field Configs"
      closeForm={closeForm}
      idToEdit={idToEdit}
      handleFormSubmit={formik.handleSubmit}
    >
      <Components.QICustomSelect
        label={getLocalizedString("asset_type", "Asset Type")}
        value={formik.values.asset_types?.[0] || ""}
        onChange={(value) => {
          formik.setFieldValue("asset_types", [value]);
        }}
        error={formik.touched.asset_types && formik.errors.asset_types}
        labelClassName="asset-type"
      >
        {assetTypeOptions.map((type) => {
          return (
            <li key={type?.id} value={type?.id}>
              {type?.name}
            </li>
          );
        })}
      </Components.QICustomSelect>

      <Components.QIInput
        label={getLocalizedString("name", "Name")}
        placeholder={getLocalizedString("enter_name", "Enter name (e.g., vin)")}
        {...formik.getFieldProps("name")}
        error={formik.touched.name && formik.errors.name}
      />

      <Components.QIInput
        label={getLocalizedString("label", "Label")}
        placeholder={getLocalizedString("enter_label", "Enter label (e.g., VIN)")}
        {...formik.getFieldProps("label")}
        error={formik.touched.label && formik.errors.label}
      />

      <Components.QICustomSelect
        label={getLocalizedString("field_type", "Field Type")}
        {...formik.getFieldProps("field_type")}
        onChange={(value) => {
          formik.setFieldValue("field_type", value);
        }}
        error={formik.touched.field_type && formik.errors.field_type}
        labelClassName="field-type"
      >
        {fieldTypeOptions.map((type) => {
          return (
            <li key={type?.id} value={type?.id}>
              {type?.name}
            </li>
          );
        })}
      </Components.QICustomSelect>

      <Components.QIInput
        label={getLocalizedString("placeholder", "Placeholder")}
        placeholder={getLocalizedString("enter_placeholder_text", "Enter placeholder text (e.g., Please enter VIN)")}
        {...formik.getFieldProps("placeholder")}
        error={formik.touched.placeholder && formik.errors.placeholder}
      />

      <h4 className="form-section-title font-bold mt-4 mb-3">{getLocalizedString("validation", "Validation")}</h4>

      <div className="qi-input">
        <Components.QICheckBox
          label={getLocalizedString("required", "Required")}
          checked={formik.values.required}
          value={!formik.values.required}
          onChange={(value) => formik.setFieldValue("required", value)}
        />
      </div>

      <div className="flex gap-3 justify-between">
        <Components.QIInput
          label={getLocalizedString("min", "Min")}
          placeholder={getLocalizedString("enter_min", "e.g., 6")}
          type="number"
          {...formik.getFieldProps("min")}
          error={formik.touched.min && formik.errors.min}
        />

        <Components.QIInput
          label={getLocalizedString("max", "Max")}
          placeholder={getLocalizedString("enter_max", "e.g., 17")}
          type="number"
          {...formik.getFieldProps("max")}
          error={formik.touched.max && formik.errors.max}
        />
      </div>

      <div className="flex gap-4 justify-between">
        <Components.QIInput
          label={getLocalizedString("regex", "Regex (Optional)")}
          placeholder={getLocalizedString("enter_regex", "Enter regex pattern (e.g., ^[A-Z0-9]{17}$)")}
          value={formik.values.regex_pattern}
          onChange={(e) => {
            formik.setFieldValue("regex_pattern", e.target.value);
            setRegexTemplate("custom");
            validateRegexPattern(e.target.value);
          }}
          onBlur={formik.handleBlur}
          error={regexError || (formik.touched.regex_pattern && formik.errors.regex_pattern)}
          className="w-full"
        />

        <Components.QICustomSelect
          value={regexTemplate}
          onChange={handleRegexTemplateChange}
          labelClassName="regex-template"
          className="custom-field_regex-template"
        >
          {regexTemplates.map((template) => (
            <li key={template.id} value={template.id}>
              {template.name}
            </li>
          ))}
        </Components.QICustomSelect>
      </div>
    </FormContainer>
  );
};
